import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { ModalHeaderButton } from '@components/modal-header-button';
import { analytics } from '../../../analytics';
import { breakpoints, navigation } from '../../../constants';
import { Drawer } from '../../drawer';
import { Flex } from '../../grid';
import { Subheading } from '../../typography';
import { NavLink } from '../../nav-link';
import { useIsMinWidth } from '../../../hooks/use-is-min-width';
import { useNavigationItems } from '../../../hooks/use-navigation-items';
import { useFeature } from '../../../hooks/use-feature';

const Nav = styled(Flex)`
  width: 100%;

  @media (min-width: ${breakpoints.sm}) {
    width: auto;
  }
`;

const Divider = styled.div`
  @media (min-width: ${breakpoints.md}) {
    background-color: ${({ theme }) => theme.colors.warmWhite3};
    height: 85%;
    width: 1px;
  }
`;

const ContentWrapper = styled(Flex)`
  flex-wrap: wrap;
  justify-content: center;
  max-width: 972px;
  width: 100%;
`;

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 100%;
  }
`;

const MobileNavigation = ({ isOpen, onClose }) => {
  const primaryNavigationItems = useNavigationItems('primary').filter(item => item.label !== 'Catering');
  const secondaryNavigationItems = useNavigationItems('secondary');
  // filter out duplicate items that appear in both the primary and secondary nav
  const filteredSecondaryNavigationItems = secondaryNavigationItems.filter(item => {
    return !primaryNavigationItems.find(({ href }) => item.href === href);
  });
  const anchor = useIsMinWidth('sm') ? 'top' : 'left';
  const cateringEnabled = useFeature('catering-enabled');

  return (
    <StyledDrawer open={isOpen} onClose={onClose} anchor={anchor}>
      <Flex
        justifyContent="space-between"
        flexDirection="column"
        pb={{ xs: '72px', md: '105px' }}
        minHeight="100%"
        backgroundColor="warmWhite100"
      >
        <Flex flexDirection="column" backgroundColor="warmWhite100">
          <Flex p={{ xs: '20px', md: '32px 32px 25px 32px' }} width="100%" justifyContent="flex-end">
            <ModalHeaderButton onClick={onClose} dark />
          </Flex>
          <Flex width="100%" px="32px" justifyContent="center">
            <ContentWrapper>
              <Nav flexDirection="column" flex="1 1 auto" py={{ md: '24px' }} mr={{ xs: '0', md: '64px' }}>
                <NavLink
                  href={navigation.MENU}
                  py={{ xs: '16px', md: '24px' }}
                  borderBottom="1px solid"
                  borderColor="warmWhite3"
                  onClick={() =>
                    analytics.track(ANALYTICS_EVENTS.HEADER_NAV_ITEM_CLICKED, {
                      text: 'Order Food',
                    })
                  }
                >
                  <Subheading as="span" display="flex">
                    Order Food
                  </Subheading>
                </NavLink>
                {cateringEnabled && (
                  <NavLink
                    href={navigation.CATERING}
                    py={{ xs: '16px', md: '24px' }}
                    borderBottom="1px solid"
                    borderColor="warmWhite3"
                    onClick={() =>
                      analytics.track(ANALYTICS_EVENTS.HEADER_NAV_ITEM_CLICKED, {
                        text: 'Order Catering',
                      })
                    }
                  >
                    <Subheading as="span" display="flex">
                      Order Catering
                    </Subheading>
                  </NavLink>
                )}
                {primaryNavigationItems.slice(1).map(({ label, href }) => (
                  <NavLink
                    onClick={() =>
                      analytics.track(ANALYTICS_EVENTS.HEADER_NAV_ITEM_CLICKED, {
                        text: label,
                      })
                    }
                    href={href}
                    key={href}
                    py={{ xs: '16px', md: '24px' }}
                    borderBottom="1px solid"
                    borderColor="warmWhite3"
                  >
                    <Subheading as="span" display="flex">
                      {label}
                    </Subheading>
                  </NavLink>
                ))}
              </Nav>
              <Divider />
              <Nav
                flex="1 1 auto"
                flexDirection={{ xs: 'row', md: 'column' }}
                justifyContent={{ md: 'center' }}
                flexWrap="wrap"
                maxWidth={{ md: '404.5px' }}
                ml={{ xs: '0', md: '64px' }}
                gap="24px"
                pt={{ xs: '40px', md: '0' }}
              >
                {filteredSecondaryNavigationItems.map(({ label, href, target }) => (
                  <NavLink
                    variant="standard-link"
                    textTransform="uppercase"
                    key={href}
                    onClick={() =>
                      analytics.track(ANALYTICS_EVENTS.HEADER_NAV_ITEM_CLICKED, {
                        text: label,
                      })
                    }
                    href={href}
                    target={target}
                  >
                    {label}
                  </NavLink>
                ))}
              </Nav>
            </ContentWrapper>
          </Flex>
        </Flex>
      </Flex>
    </StyledDrawer>
  );
};

MobileNavigation.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export { MobileNavigation };
